import { Link } from "react-router-dom";
import {
  BookOpen, ChevronRight, ClipboardList, Factory, FileText, Landmark,
  Package, Receipt, Settings, ShieldCheck, Sparkles, Users, Wrench,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const GUIDES = [
  {
    title: "Getting started",
    icon: BookOpen,
    color: "text-blue-700 bg-blue-50 border-blue-100",
    items: [
      "Sign in with the account provided by your administrator.",
      "Use the left sidebar to open the modules available to your role.",
      "Use Ctrl + K to search modules and common workspaces quickly.",
      "A Degraded status means SAP data may be delayed or unavailable; retry after checking the connection.",
    ],
  },
  {
    title: "Overview",
    icon: Sparkles,
    path: "/",
    color: "text-indigo-700 bg-indigo-50 border-indigo-100",
    items: [
      "Choose a reporting period, then select Refresh to load the latest KPI data.",
      "Use the Summary for key exceptions and the other tabs for scorecards, trends, risks, and efficiency.",
      "Select an alert to investigate the related module.",
    ],
  },
  {
    title: "Planning and production",
    icon: Factory,
    path: "/planning",
    color: "text-violet-700 bg-violet-50 border-violet-100",
    items: [
      "Use Planning to review materials, sales orders, production plans, schedules, forecasts, and production orders.",
      "Use Daily Report to select a posting-date range, optionally refine filters, then select Go.",
      "Exports contain the currently loaded reporting data and filters.",
    ],
  },
  {
    title: "Finance and GST",
    icon: Landmark,
    path: "/financial-analyst",
    color: "text-emerald-700 bg-emerald-50 border-emerald-100",
    items: [
      "Use Finance for live financial analysis and report exports.",
      "Use GST Report to review GST data for the selected company and reporting period.",
      "If a report shows no data, confirm the selected dates and SAP availability before retrying.",
    ],
  },
  {
    title: "Supply chain",
    icon: Package,
    path: "/supply-chain",
    color: "text-amber-700 bg-amber-50 border-amber-100",
    items: [
      "Select Last 30, 60, 90, or 180 Days and choose Go to refresh period-based metrics.",
      "Use Dashboard for inventory health, delivery performance, and material flow.",
      "Use Delivery Tracking to search and filter inbound and outbound deliveries.",
      "Ask the AI Analyst focused questions about inventory, delivery performance, and stockout risk.",
    ],
  },
  {
    title: "Purchasing",
    icon: Receipt,
    path: "/purchasing",
    color: "text-cyan-700 bg-cyan-50 border-cyan-100",
    items: [
      "Review procurement KPIs, purchase orders, supplier performance, and spend data.",
      "Apply page filters before exporting so the export matches the view you are reviewing.",
      "Investigate overdue or incomplete orders before making operational decisions.",
    ],
  },
];

const ADMIN_GUIDES = [
  { title: "Settings", icon: Settings, path: "/settings", text: "Configure SAP connections and application-level settings. Test SAP connections before activating them." },
  { title: "Users", icon: Users, path: "/users", text: "Create users, assign roles, and deactivate access when it is no longer required." },
  { title: "AI Configuration", icon: Wrench, path: "/ai-config", text: "Save and test the AI provider key before using AI Analyst features." },
  { title: "Audit Log", icon: ClipboardList, path: "/audit-log", text: "Review activity history when investigating configuration or access changes." },
];

const UserManual = () => {
  const { isAdmin } = useAuth();

  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-4 py-7 sm:px-6 lg:px-8" data-testid="user-manual-page">
      <section className="overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-indigo-50 p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-blue-700"><BookOpen className="h-4 w-4" /> MESPROSOFT USER MANUAL</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Use your SAP data with confidence</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600 sm:text-base">This guide explains where to find each feature, how to load live SAP data, and what to check when a report is unavailable.</p>
          </div>
          <a href="#support" className="inline-flex shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-50">Need help?</a>
        </div>
      </section>

      <section aria-labelledby="quick-start-title" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 id="quick-start-title" className="text-lg font-bold text-slate-900">Quick start</h2>
        <ol className="mt-4 grid gap-3 sm:grid-cols-3">
          {["Choose a module from the sidebar.", "Select the date range and filters for your report.", "Select Go or Refresh, then export or investigate the results."].map((step, index) => <li key={step} className="flex gap-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-700"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{index + 1}</span><span>{step}</span></li>)}
        </ol>
      </section>

      <section aria-labelledby="guides-title">
        <div className="mb-4"><h2 id="guides-title" className="text-xl font-bold text-slate-900">Module guides</h2><p className="mt-1 text-sm text-slate-500">Open a module directly from any guide heading.</p></div>
        <div className="grid gap-4 md:grid-cols-2">
          {GUIDES.map(({ title, icon: Icon, path, color, items }) => <article key={title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-start justify-between gap-4"><div className="flex items-center gap-3"><div className={`rounded-xl border p-2.5 ${color}`}><Icon className="h-5 w-5" /></div><h3 className="font-bold text-slate-900">{title}</h3></div>{path && <Link to={path} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">Open <ChevronRight className="h-4 w-4" /></Link>}</div><ul className="mt-4 space-y-2 text-sm leading-5 text-slate-600">{items.map((item) => <li key={item} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />{item}</li>)}</ul></article>)}
        </div>
      </section>

      {isAdmin && <section aria-labelledby="admin-title" className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 shadow-sm sm:p-6"><div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-700" /><h2 id="admin-title" className="text-lg font-bold text-slate-900">Administrator guide</h2></div><p className="mt-1 text-sm text-slate-600">These pages affect access, integrations, and system-wide behavior.</p><div className="mt-4 grid gap-3 md:grid-cols-2">{ADMIN_GUIDES.map(({ title, icon: Icon, path, text }) => <Link to={path} key={title} className="group rounded-xl border border-amber-200 bg-white p-4 transition-colors hover:border-amber-400"><div className="flex items-center gap-2 font-semibold text-slate-900"><Icon className="h-4 w-4 text-amber-700" />{title}<ChevronRight className="ml-auto h-4 w-4 text-slate-400 group-hover:text-amber-700" /></div><p className="mt-2 text-sm leading-5 text-slate-600">{text}</p></Link>)}</div></section>}

      <section id="support" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"><div className="flex items-start gap-3"><FileText className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" /><div><h2 className="font-bold text-slate-900">When data is unavailable</h2><p className="mt-1 text-sm leading-6 text-slate-600">Confirm the date range, filters, and SAP connection status. A timeout or degraded warning means the screen may show partial data. Retry the request; if it persists, share the page name, dates, filters, and warning message with your administrator or support team.</p></div></div></section>
    </main>
  );
};

export default UserManual;
